import React, { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { useQuery } from '@apollo/client';
import { RadioGroupField } from 'components/form';
import { useForm, useWatch } from 'react-hook-form';
import { ListEmpty, ListLoading } from 'components/shared/List';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import DownloadIcon from '@mui/icons-material/Download';

import { bpfAPI } from '../../api';
import { collectReportFilters } from '../../lib';
import { ReportFilters } from './ReportsFilters';
import { ReportsSummary } from './ReportsSummary';
import { GROUP_BY_OPTIONS } from '../../constants';
import { FullReportsListing } from './FullReportsListing';
import { useStagesReportDefaultFilters } from '../../hooks/useStagesReportDefaultFilters';
import {
  BPFReportAPINameSpase,
  FullReportResponse,
  ReportListingFilterLabels,
  ReportsListingFilters,
} from '../../types';
import { exportOpportunityReportToCsv, GroupByValue } from './exportToCsv';

export const FullReports = () => {
  const { initialFilters } = useStagesReportDefaultFilters();

  const [filters, setFilters] = useState<ReportsListingFilters>(initialFilters);
  const [exportFilterLabels, setExportFilterLabels] = useState<ReportListingFilterLabels>({});

  const groupByForm = useForm<{ groupBy: GroupByValue }>({
    defaultValues: { groupBy: 'sales_representative_id' },
  });
  const groupBy = useWatch({ control: groupByForm.control, name: 'groupBy' });

  const variables = {
    input: { groupBy, filters: collectReportFilters(filters) },
  };

  const { data, loading, error, previousData } = useQuery<BPFReportAPINameSpase<FullReportResponse>>(
    bpfAPI.report.getReport,
    {
      notifyOnNetworkStatusChange: true,
      variables: variables,
    }
  );

  const summary = (data ?? previousData)?.bpf?.report?.result?.entity?.summary;
  const list = (data ?? previousData)?.bpf?.report?.result?.entity?.list ?? [];

  const handleFiltersSubscribe = (filters: ReportsListingFilters, labels: ReportListingFilterLabels) => {
    setFilters(filters);
    setExportFilterLabels(labels);
  };

  const handleExportCsv = () => {
    exportOpportunityReportToCsv(list, groupBy, filters, exportFilterLabels);
  };

  return (
    <RenderWithFallback isLoading={loading && !previousData} error={error} fallBack={<ListLoading />}>
      <Stack gap="1rem" height={!list.length ? 'calc(100% - 1rem)' : 'auto'}>
        <ReportFilters onSubscribe={handleFiltersSubscribe} />

        <RenderWithCondition condition={!!list.length}>
          <ReportsSummary summary={summary} />
        </RenderWithCondition>

        <Stack direction="row" alignItems="center" justifyContent="space-between" gap="1rem">
          <RadioGroupField
            row
            name="groupBy"
            label="Group by:"
            options={GROUP_BY_OPTIONS}
            control={groupByForm.control}
          />

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            disabled={!list.length || loading}
          >
            Export CSV
          </Button>
        </Stack>

        <Box sx={{ boxShadow: ({ shadows }) => shadows[1], height: '100%' }}>
          <RenderWithCondition condition={!!list.length} fallBack={<ListEmpty />}>
            <FullReportsListing list={list} loading={loading} />
          </RenderWithCondition>
        </Box>
      </Stack>
    </RenderWithFallback>
  );
};
